import { AxisCode } from '@atlas/types'
import type { FilterAxis, FilterSelection } from '@atlas/ui-components'
import { useMemo, useState } from 'react'
import type { Prompt, PromptLifecycle, PromptType } from '@/domain/prompt.types'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { useApplicablePrompts, usePrompts } from '@/hooks/usePrompts'

/**
 * The axis groups the prompt rail opens with. Every other pickable axis is one click
 * away under "Add variable", so this is a starting point rather than a limit.
 */
export const DEFAULT_PROMPT_FILTER_AXES: string[] = [AxisCode.BRAND, AxisCode.GEOGRAPHY, AxisCode.SPECIES]

const matchesQuery = (prompt: Prompt, search: string) => {
	const q = search.trim().toLowerCase()
	if (!q) return true
	return (
		prompt.name.toLowerCase().includes(q) ||
		prompt.code.toLowerCase().includes(q) ||
		(prompt.description ?? '').toLowerCase().includes(q)
	)
}

/** Axis codes with at least one value selected. */
const activeAxes = (selection: FilterSelection) =>
	Object.fromEntries(Object.entries(selection).filter(([, values]) => values.length > 0))

/**
 * Every filter the prompt library offers, and the rows that survive them.
 *
 * Classification filtering is SERVER-side (the resolution query), while type,
 * lifecycle and text are client-side over whichever row set came back. The split is
 * forced rather than chosen: the list response carries no classification, so an axis
 * filter has nothing to match on locally — and resolution is the shared service that
 * already knows how to answer it.
 */
export function usePromptFilters() {
	const [search, setSearch] = useState('')
	const [types, setTypes] = useState<PromptType[]>([])
	const [lifecycles, setLifecycles] = useState<PromptLifecycle[]>([])
	const facets = useFacetSelection()

	const { shownAxisCodes, selection } = facets
	const context = activeAxes(selection)
	const hasFacets = Object.keys(context).length > 0

	const all = usePrompts()
	const applicable = useApplicablePrompts(context, hasFacets)
	// The facet query REPLACES the list read rather than intersecting with it: it
	// returns the authoritative answer for that tuple, and intersecting would drop a
	// prompt the resolution query matched leniently.
	const source = hasFacets ? applicable : all

	const { axes, isLoading: axesLoading } = useRailAxes(shownAxisCodes)

	const prompts = source.data ?? []
	const filtered = useMemo(
		() =>
			prompts.filter(
				(p) =>
					matchesQuery(p, search) &&
					(types.length === 0 || types.includes(p.type)) &&
					(lifecycles.length === 0 || lifecycles.includes(p.lifecycle)),
			),
		[prompts, search, types, lifecycles],
	)

	return {
		search,
		setSearch,
		types,
		setTypes,
		lifecycles,
		setLifecycles,
		filtered,
		isLoading: source.isPending,
		isError: source.isError,
		// Facet rail
		axes,
		axesLoading,
		shownAxisCodes,
		selection,
		toggleValue: facets.toggleValue,
		addAxis: facets.addAxis,
		removeAxis: facets.removeAxis,
	}
}

/** Which axis groups are shown, and which of their values are picked. */
function useFacetSelection() {
	const [shownAxisCodes, setShownAxisCodes] = useState<string[]>(DEFAULT_PROMPT_FILTER_AXES)
	const [selection, setSelection] = useState<FilterSelection>({})

	return {
		shownAxisCodes,
		selection,
		toggleValue: (axisCode: string, valueCode: string) =>
			setSelection((prev) => {
				const current = prev[axisCode] ?? []
				const next = current.includes(valueCode) ? current.filter((v) => v !== valueCode) : [...current, valueCode]
				return { ...prev, [axisCode]: next }
			}),
		addAxis: (axisCode: string) => setShownAxisCodes((prev) => (prev.includes(axisCode) ? prev : [...prev, axisCode])),
		removeAxis: (axisCode: string) => {
			setShownAxisCodes((prev) => prev.filter((c) => c !== axisCode))
			// Dropping the group must drop its selection too, or the list stays filtered
			// by a facet the user can no longer see or clear.
			setSelection((prev) => ({ ...prev, [axisCode]: [] }))
		},
	}
}

/**
 * The rail's axes: every pickable axis is OFFERED, but only a shown one carries its
 * values — the reveal-on-demand shape the other rails in the app use.
 */
function useRailAxes(shownAxisCodes: string[]): { axes: FilterAxis[]; isLoading: boolean } {
	const { axes: registryAxes, isLoading } = useAxesWithValues()

	const axes: FilterAxis[] = registryAxes.map((axis) => ({
		code: axis.code,
		label: axis.label,
		options: shownAxisCodes.includes(axis.code) ? axis.options : [],
	}))

	return { axes, isLoading }
}
